import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AppIcon from './AppIcon';

function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="app animated-dots">
      {/* Animated dots */}
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>

      {/* Main Content */}
      <div className="main-content">
        {/* Header with icon */}
        <div className="page-header">
          <Link to="/" className="page-icon-container">
            <AppIcon size={70} />
          </Link>
        </div>

        {/* Privacy Content */}
        <div className="page-content">
          <h1 className="page-title">Privacy Policy</h1>
          <p className="section-text" style={{ marginBottom: '2rem', fontStyle: 'italic' }}>
            Last Updated: November 24, 2025
          </p>
          
          <div className="content-section">
            <section className="mb-8">
              <h2 className="section-title">Our Privacy Commitment</h2>
              <p className="section-text">
                Controller-App is designed with your privacy as a top priority. We believe professional tools should be powerful without compromising your privacy. This policy explains our minimal data collection practices.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="section-title">1. No User Accounts or Personal Data</h2>
              <p className="section-text">
                Controller-App does not require account creation, user login, or authentication of any kind. We do not collect, store, or maintain any personally identifiable information (PII) about our users, including:
              </p>
              <ul className="section-list">
                <li>No names, email addresses, or contact information</li>
                <li>No passwords or authentication credentials</li>
                <li>No user profiles or account data</li>
                <li>No user-generated content stored on our servers</li>
                <li>No tracking of individual users across sessions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="section-title">2. Data Collection - Not Linked to You</h2>
              <p className="section-text">
                Controller-App collects limited, anonymous data to improve app quality and user experience. All collected data is <strong>not linked to your identity</strong> and cannot be traced back to you personally.
              </p>
              
              <h3 className="section-subtitle" style={{ marginTop: '1.5rem', fontSize: '1.1rem', fontWeight: 600 }}>
                Usage Data
              </h3>
              <p className="section-text">
                We collect anonymous usage statistics through Google Analytics to understand how features are used:
              </p>
              <ul className="section-list">
                <li>Product interactions (which features are used, button taps, screen views)</li>
                <li>App feature usage frequency (e.g., how often DMX calculator or presets are used)</li>
                <li>Session duration and app usage patterns</li>
                <li>General geographic region (country-level only, not specific location)</li>
              </ul>
              
              <h3 className="section-subtitle" style={{ marginTop: '1.5rem', fontSize: '1.1rem', fontWeight: 600 }}>
                Diagnostics Data
              </h3>
              <p className="section-text">
                We automatically collect diagnostic information to identify and fix issues:
              </p>
              <ul className="section-list">
                <li><strong>Crash Data:</strong> Information about app crashes to help us fix bugs</li>
                <li><strong>Performance Data:</strong> App performance metrics (load times, response times, memory usage)</li>
                <li><strong>Other Diagnostic Data:</strong> Technical logs, error messages, and device information (device type, iOS version, screen resolution)</li>
              </ul>
              
              <p className="section-text" style={{ marginTop: '1.5rem' }}>
                <strong>Important:</strong> All of this data is anonymized, aggregated, and cannot be linked to you as an individual user. We use this information solely to improve app stability, performance, and user experience.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="section-title">3. Local Data Storage</h2>
              <p className="section-text">
                All app data, including your settings, presets, fixture library, and templates, is stored locally on your device only. This data:
              </p>
              <ul className="section-list">
                <li>Never leaves your device</li>
                <li>Is not backed up to our servers</li>
                <li>Is not accessible to us or any third parties</li>
                <li>Can only be accessed by you on your device</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="section-title">4. Network Connections</h2>
              <p className="section-text">
                When you use Controller-App's networking features (Telnet or OSC), connections are made:
              </p>
              <ul className="section-list">
                <li>Directly between your device and your lighting console</li>
                <li>On your local network only</li>
                <li>Without routing through our servers</li>
                <li>With no monitoring or logging by us</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="section-title">5. Third-Party Services</h2>
              <p className="section-text">
                Controller-App integrates with the following third-party services:
              </p>
              <ul className="section-list">
                <li><strong>Google Analytics:</strong> Anonymous usage analytics for product interaction tracking</li>
                <li><strong>Apple App Store:</strong> Subscription management through Apple's payment system (Apple's privacy policy applies)</li>
                <li><strong>iOS Crash Reporting:</strong> Apple's built-in crash reporting and diagnostics (Apple's privacy policy applies)</li>
              </ul>
              <p className="section-text" style={{ marginTop: '1rem' }}>
                We do not control and are not responsible for the privacy practices of these third-party services. Please review their respective privacy policies. All data collected by these services is anonymous and not linked to your identity.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="section-title">6. Children's Privacy</h2>
              <p className="section-text">
                Controller-App is rated 4+ and does not knowingly collect any information from children. Since we don't collect personal information from anyone, we don't collect it from children either.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="section-title">7. Data Security</h2>
              <p className="section-text">
                Since we don't collect or store personal data, there is no personal data at risk. Your app data remains on your device and is protected by your device's security measures (passcode, Face ID, Touch ID).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="section-title">8. Your Rights</h2>
              <p className="section-text">
                Since we don't collect personal information:
              </p>
              <ul className="section-list">
                <li>There is no personal data to access, correct, or delete</li>
                <li>You are not tracked across sessions</li>
                <li>You can use the app completely anonymously</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="section-title">9. Changes to This Policy</h2>
              <p className="section-text">
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. Continued use of the app after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="section-title">10. Contact Us</h2>
              <p className="section-text">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at{' '}
                <a href="mailto:support@controller-app.com" className="link-text">
                  support@controller-app.com
                </a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="section-title">11. Terms of Service</h2>
              <p className="section-text">
                This Privacy Policy is part of our Terms of Service. Please review our{' '}
                <Link to="/terms" className="link-text">
                  Terms of Service
                </Link>.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-divider"></div>
          <div className="footer-links">
            <Link to="/terms" className="footer-link">
              Terms of Service
            </Link>
            <Link to="/privacy" className="footer-link">
              Privacy Policy
            </Link>
          </div>
          <p>© 2025 ControllerApp. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPage;
